import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, Timestamp, orderBy } from 'firebase/firestore';

export default function ReportsTab({ user, centerData }) {
  const [stats, setStats]   = useState({ totalJobs: 0, totalRevenue: 0, avgRating: 0, topService: '', busyDay: '', completionRate: 0 });
  const [monthly, setMonthly] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear]       = useState(new Date().getFullYear());

  useEffect(() => { if (centerData?.id) loadReports(); else setLoading(false); }, [centerData, year]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const start = new Date(year, 0, 1);
      const end   = new Date(year, 11, 31, 23, 59, 59);
      const q = query(
        collection(db, 'washRequests'),
        where('serviceCenterId', '==', centerData.id),
        where('scheduledAt', '>=', Timestamp.fromDate(start)),
        where('scheduledAt', '<=', Timestamp.fromDate(end)),
        orderBy('scheduledAt', 'asc')
      );
      const snap = await getDocs(q);
      const jobs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const completed = jobs.filter(j => j.centerStatus === 'completed');

      // Monthly breakdown
      const monthData = Array(12).fill(0).map((_, i) => ({ month: i, jobs: 0, revenue: 0 }));
      completed.forEach(j => {
        const m = j.scheduledAt?.toDate?.()?.getMonth();
        if (m !== undefined) {
          monthData[m].jobs++;
          monthData[m].revenue += j.quotedCenterAmount || 0;
        }
      });
      setMonthly(monthData);

      // Top service
      const svcCount = {};
      completed.forEach(j => (j.selectedServices || []).forEach(s => { svcCount[s] = (svcCount[s] || 0) + 1; }));
      const topSvc = Object.entries(svcCount).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';

      // Busy day
      const dayCount = Array(7).fill(0);
      completed.forEach(j => { const d = j.scheduledAt?.toDate?.()?.getDay(); if (d !== undefined) dayCount[d]++; });
      const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
      const busyDay = days[dayCount.indexOf(Math.max(...dayCount))];

      setStats({
        totalJobs:      completed.length,
        totalRevenue:   completed.reduce((s, j) => s + (j.quotedCenterAmount || 0), 0),
        avgRating:      centerData.ratingAverage || 0,
        topService:     topSvc,
        busyDay,
        completionRate: jobs.length > 0 ? Math.round((completed.length / jobs.length) * 100) : 0,
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const maxRevenue = Math.max(...monthly.map(m => m.revenue), 1);

  return (
    <div style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 900 }}>Reports & Analytics</h1>
          <p style={{ color: '#6b7280', marginTop: 4 }}>Performance insights for your service center</p>
        </div>
        <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, fontWeight: 600, background: '#fff', cursor: 'pointer' }}>
          {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14, marginBottom: 28 }}>
        {[
          { label: 'Total Jobs', value: stats.totalJobs, color: '#F97316' },
          { label: 'Total Revenue', value: '$' + stats.totalRevenue.toFixed(0), color: '#16a34a' },
          { label: 'Avg Rating', value: stats.avgRating > 0 ? stats.avgRating.toFixed(1) + ' ⭐' : 'No data', color: '#f59e0b' },
          { label: 'Completion Rate', value: stats.completionRate + '%', color: '#3b82f6' },
          { label: 'Top Service', value: stats.topService, color: '#7c3aed', small: true },
          { label: 'Busiest Day', value: stats.busyDay, color: '#ec4899', small: true },
        ].map((s, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: s.small ? 14 : 22, fontWeight: 900, color: '#111827' }}>{loading ? '...' : s.value}</div>
          </div>
        ))}
      </div>

      {/* Revenue bar chart */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 800, marginBottom: 20 }}>Monthly Revenue {year}</h3>
        {loading ? <p style={{ color: '#6b7280' }}>Loading...</p> : (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 180 }}>
            {monthly.map((m, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600 }}>{m.revenue > 0 ? '$' + m.revenue.toFixed(0) : ''}</div>
                <div style={{ width: '100%', background: m.revenue > 0 ? '#F97316' : '#f3f4f6', borderRadius: '4px 4px 0 0', height: Math.max((m.revenue / maxRevenue) * 140, m.revenue > 0 ? 4 : 0), transition: 'height 0.3s' }} />
                <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600 }}>{MONTHS[m.month]}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Monthly table */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <h3 style={{ fontSize: 16, fontWeight: 800, marginBottom: 16 }}>Monthly Breakdown</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #f3f4f6' }}>
              {['Month', 'Jobs', 'Revenue', 'Avg per Job'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {monthly.map((m, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f9fafb', opacity: m.jobs === 0 ? 0.4 : 1 }}>
                <td style={{ padding: '12px', fontSize: 13, fontWeight: 600 }}>{MONTHS[m.month]}</td>
                <td style={{ padding: '12px', fontSize: 13 }}>{m.jobs}</td>
                <td style={{ padding: '12px', fontSize: 13, fontWeight: m.revenue > 0 ? 700 : 400, color: m.revenue > 0 ? '#16a34a' : '#9ca3af' }}>${m.revenue.toFixed(2)}</td>
                <td style={{ padding: '12px', fontSize: 13 }}>{m.jobs > 0 ? '$' + (m.revenue / m.jobs).toFixed(2) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}